import React from 'react';
import { connect } from 'react-redux';
import { Type } from '@spotify-internal/encore-creator-web';
import styled from 'styled-components';
import { loadPodcastDemographics } from '../../../store/podcastDemographics';
import styles from './index.module.scss';
import {
  selectPodcastDemographicsStatus,
  selectPodcastDemographics,
  selectDateRange,
  selectFeatures,
  selectMe,
  LOADING,
  ERROR,
} from '../../../store/selectors';

import { AgeDemographicsChart } from '../../Shared/Charts/AgeDemographicsChart';
import { GenderDemographicsChart } from '../../Shared/Charts/GenderDemographicsChart';
import { CountryListTable } from '../../Shared/Tables/CountryListTable';
import { LoadingIndicator } from '../../Shared/LoadingIndicator';
import { TopArtistsChart } from './TopArtistsChart';
import {
  Me,
  ReduxState,
  PodcastAudienceDemographics,
  DateRangeWithLabel,
  CountryDemographics,
  FeatureMap,
} from '../../../types';
import { MapChart, MapChartData } from '../../../vendor/MapChart';
import { useWindowWidth } from '../../../hooks/useWindowWidth';
import { FormattedMessage } from 'react-intl';

const ScreenChartMin = 1625;

interface Props {
  podcastDemographics?: PodcastAudienceDemographics;
  status?: 'LOADING' | 'ERROR';
  podcastId: string;
  loadPodcastDemographics(id: string): void;
  dateRange?: DateRangeWithLabel;
  features: FeatureMap;
  me: Me;
}

export function PodcastDemographicsContainer(props: Props) {
  const [highlightCountry, setHighlightCountry] = React.useState<string>();
  const windowWidth = useWindowWidth();

  React.useEffect(() => {
    props.loadPodcastDemographics(props.podcastId);
  }, [props.dateRange]);

  if (props.status === ERROR) {
    return <span data-qa="podcast-demographics-error" />;
  }

  if (!props.podcastDemographics && props.status !== LOADING) {
    return null;
  }

  const {
    genderDemographicsByAgeGroup: ageData = [],
    percentageByGender: genderData = [],
    countryDemographics = [],
  } = props.podcastDemographics || {};

  return (
    <div data-qa="podcast-demographics-charts">
      <div className={styles.chart}>
        <Type.h1 variant={Type.heading3}>
          <FormattedMessage id="podcast.audience.gender" defaultMessage="Gender" />
        </Type.h1>
        {props.status === LOADING ? (
          <Loader />
        ) : (
          <GenderDemographicsChart genderData={genderData} />
        )}
      </div>
      <div className={styles.chart}>
        <Type.h1 variant={Type.heading3}>
          <FormattedMessage id="podcast.audience.age" defaultMessage="Age" />
        </Type.h1>
        {props.status === LOADING ? <Loader /> : <AgeDemographicsChart ageData={ageData} />}
      </div>

      <TopArtistsChart showId={props.podcastId} />

      {countryDemographics.length > 0 && (
        <div className={styles.chart}>
          <Type.h1 variant={Type.heading3}>
            <FormattedMessage id="podcast.audience.country" defaultMessage="Country" />
          </Type.h1>
          <CountryGrid>
            <OverflowWrapper>
              <CountryListTable countryData={countryDemographics} />
            </OverflowWrapper>
            {process.env.NODE_ENV !== 'test' &&
              /* istanbul ignore next */ windowWidth > ScreenChartMin && (
                /* istanbul ignore next */ <MapChart
                  data={translateCountryDemographicsToChartData(countryDemographics)}
                  stat="streams"
                  legendHeight={40}
                  width={960}
                  height={500}
                  setHighlightCountry={setHighlightCountry}
                  highlightCountry={highlightCountry}
                />
              )}
          </CountryGrid>
        </div>
      )}
    </div>
  );
}

const CountryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  margin-bottom: 16px;

  @media (min-width: ${ScreenChartMin}px) {
    grid-template-columns: 1fr minmax(auto, 960px);
  }
`;

const OverflowWrapper = styled.div`
  height: 500px;
  overflow: auto;
`;

function Loader() {
  return (
    <div style={{ width: '100%', height: '350px' }}>
      <LoadingIndicator />
    </div>
  );
}

/* istanbul ignore next */
function translateCountryDemographicsToChartData(countryDemographics: CountryDemographics[]) {
  return countryDemographics.reduce<MapChartData>((acc, { count, code, name }, index) => {
    acc[code] = {
      name,
      count,
      formatted_count: new Intl.NumberFormat().format(count),
      rank: index + 1,
    };

    return acc;
  }, {});
}

function mapStateToProps(state: ReduxState) {
  return {
    status: selectPodcastDemographicsStatus(state),
    podcastDemographics: selectPodcastDemographics(state),
    dateRange: selectDateRange(state),
    features: selectFeatures(state),
    me: selectMe(state)!,
  };
}

const actions = {
  loadPodcastDemographics,
};

export const ConnectedPodcastDemographicsContainer = connect(
  mapStateToProps,
  actions,
)(PodcastDemographicsContainer);
